import { Table } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import type { FC } from 'react'
import { useMemo } from 'react'
import styled from 'styled-components'

import {
  fieldAcceptRate,
  type FieldLlmStats,
  formatAcceptRate,
} from './computeTicketLlmStats'

export const PerFieldTable: FC<{ fields: FieldLlmStats[] }> = ({ fields }) => {
  const columns = useMemo<ColumnsType<FieldLlmStats>>(
    () => [
      {
        title: 'Field',
        dataIndex: 'fieldName',
        ellipsis: true,
        sorter: (a, b) => a.fieldName.localeCompare(b.fieldName),
        render: (_: unknown, record) => (
          <FieldCell>
            <span>{record.fieldName}</span>
            {record.fieldName !== record.fieldId && (
              <FieldIdSubtext>{record.fieldId}</FieldIdSubtext>
            )}
          </FieldCell>
        ),
      },
      {
        title: 'Tickets',
        dataIndex: 'total',
        align: 'center',
        width: 110,
        sorter: (a, b) => a.total - b.total,
      },
      {
        title: 'Accepted',
        dataIndex: 'accepted',
        align: 'center',
        width: 110,
        sorter: (a, b) => a.accepted - b.accepted,
        render: (value: number) => <AcceptedCell>{value}</AcceptedCell>,
      },
      {
        title: 'Edited',
        dataIndex: 'edited',
        align: 'center',
        width: 110,
        sorter: (a, b) => a.edited - b.edited,
        render: (value: number) => <EditedCell>{value}</EditedCell>,
      },
      {
        title: 'Rejected',
        dataIndex: 'rejected',
        align: 'center',
        width: 110,
        sorter: (a, b) => a.rejected - b.rejected,
        render: (value: number) => <RejectedCell>{value}</RejectedCell>,
      },
      {
        title: 'Accept rate',
        align: 'right',
        width: 120,
        // No `defaultSortOrder`: the feed is already sorted by `total desc`
        // from the compute module, which surfaces the fields with the most
        // signal first. Users can click the header to re-sort by rate.
        sorter: (a, b) => fieldAcceptRate(a) - fieldAcceptRate(b),
        render: (_: unknown, record) =>
          formatAcceptRate(fieldAcceptRate(record), record.total),
      },
    ],
    []
  )

  return (
    <Table<FieldLlmStats>
      rowKey='fieldId'
      size='small'
      columns={columns}
      dataSource={fields}
      pagination={fields.length > 15 ? { pageSize: 15, size: 'small' } : false}
      locale={{ emptyText: 'No AI-populated fields in this range' }}
    />
  )
}

const FieldCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

const FieldIdSubtext = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  opacity: 0.7;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const AcceptedCell = styled.span`
  color: #52c41a;
  font-weight: 600;
`

const EditedCell = styled.span`
  color: #faad14;
  font-weight: 600;
`

const RejectedCell = styled.span`
  color: #ff4d4f;
  font-weight: 600;
`
